/* eslint-disable import/no-cycle */
/**
 * Per-language (`{LANG_}`) translation support for integration-widget config
 * fields — see docs/widgets-target-model-spec.html.
 *
 * A `config_schema` field flagged `translatable: true` carries text that must
 * vary by LANGUAGE (≠ `{var=}`, which varies by RECIPIENT). We reuse the native
 * template-translation pipeline rather than inventing a parallel one:
 *
 *  - each translatable field gets a stable `{LANG_<key>}` token baked into the
 *    widget output;
 *  - the per-language values live in the template translation store (editable in
 *    the translation panel, prefilled from the default-language field value);
 *  - at send the backend `getTemplateBodyHTML` replaces `{LANG_<key>}` with the
 *    recipient-language value (same `replaceAll` path as native text).
 *
 * The field→key mapping is persisted in `data-config[__i18n]` so re-edit /
 * re-render reuse the same keys (mirrors the native game widgets storing a
 * `translationKey` inside their `data-prizes` JSON).
 *
 * SCOPE: HTML widgets (static/dynamic). The token is baked into the rendered cell
 * via {@link buildHtmlRenderConfig} (sent to `/widgets/render` in place of the
 * literal value), resolved to the current language for display by
 * `translateDocument`, and converted back to `{LANG_}` at save by the native
 * pipeline. IMAGE widgets are NOT translated here: their `<img>` is excluded from
 * the native translator (`spm_no_translate`) so the URL/alt never become
 * translatable. Per-language image labels would need a dedicated mechanism (a
 * config-panel per-language input, or a partner `lang` param like `loyalty`) —
 * deferred. Single-language shops skip the whole mechanism.
 */
import { Translation } from '@/types';
import { UserState, Lang, getShopDefaultLang } from '@/composables/User';
import { createTranslationIdentifier } from '@/components/template-builder/utils/translate';
import {
  addTranslationsToState,
  getTranslationByKeyAndLang,
  replaceTranslationInState,
} from '@/composables/template-editor/TemplateEditor';

/** `data-config` key holding the `{ fieldKey: translationKey }` map. */
export const WIDGET_I18N_MAP_KEY = '__i18n';

/** Collect the keys of every `translatable` field across a widget `config_schema`. */
export const getTranslatableFieldKeys = (schema: any): Set<string> => {
  const out = new Set<string>();
  if (!schema) return out;
  const collect = (fields: any[]) =>
    (fields || []).forEach((f) => {
      if (f?.key && f.translatable) out.add(f.key);
    });
  collect(schema.fields);
  (schema.groups || []).forEach((g: any) => collect(g?.fields));
  (schema.style_groups || []).forEach((g: any) => collect(g?.fields));
  return out;
};

/** True when the widget declares at least one translatable field. */
export const hasTranslatableFields = (schema: any): boolean =>
  getTranslatableFieldKeys(schema).size > 0;

/** Read the `{ fieldKey: translationKey }` map from a (parsed) data-config. */
const readI18nMap = (config: any): Record<string, string> =>
  config && typeof config[WIDGET_I18N_MAP_KEY] === 'object'
    ? { ...config[WIDGET_I18N_MAP_KEY] }
    : {};

/** Ids of the shop's languages (falls back to the default language). */
const shopLangIds = (): string[] =>
  UserState.activeShop?.langs?.map((l: Lang) => l.id) ?? [getShopDefaultLang()];

/**
 * A shop with more than one language — the ONLY case where widget translation does
 * anything. On a single-language shop the whole mechanism is skipped and widgets
 * behave exactly as before (literal values, no tokens, no translation entries).
 */
export const isShopMultilingual = (): boolean => shopLangIds().length > 1;

/**
 * Ensure a `{LANG_}` translation key exists for `fieldKey`. First call registers
 * one Translation per shop language (value = the field's current default-language
 * text); later calls re-sync the default-language value to the field text (so a
 * config-panel edit reflects in the translation panel). Mutates `config[__i18n]`.
 * Returns the translation key (without the `LANG_` prefix).
 */
const ensureFieldTranslationKey = (
  config: Record<string, any>,
  fieldKey: string,
  sectionId: number,
): string => {
  const map = readI18nMap(config);
  const text = String(config[fieldKey] ?? '');
  let key = map[fieldKey];
  if (!key) {
    // `.text` suffix → same key shape as a native text translation, so it rides
    // the exact same markup branch (isManagedByAttribute), save serialization and
    // backend `{LANG_<id>.text}` replacement. fieldType 'text' to match natives.
    key = createTranslationIdentifier('text');
    map[fieldKey] = key;
    const translations: Translation[] = shopLangIds().map((lang) => ({
      section: sectionId,
      language: lang,
      fieldType: 'text',
      key,
      value: text,
      callback: (v: string) => v,
    }));
    addTranslationsToState(translations);
  } else {
    // Keep the default-language value aligned with the field (panel edit). The
    // native replaceTranslationInState also propagates to still-untranslated langs.
    const defaultLang = getShopDefaultLang();
    const existing = getTranslationByKeyAndLang(key, defaultLang);
    if (existing && existing.value !== text) {
      replaceTranslationInState(key, defaultLang, text);
    }
  }
  config[WIDGET_I18N_MAP_KEY] = map;
  return key;
};

/**
 * Build the config to POST to `/widgets/render` for an HTML widget: every
 * `translatable` field is replaced by its `{LANG_<key>}` token (so the rendered
 * cell carries the token), all other fields pass through, and the internal
 * `__i18n` map is stripped. MUTATES `config` to register/sync the translation
 * keys (adds `__i18n`) — the caller must persist the updated `data-config`.
 * When nothing is translatable (or the shop is single-language) it just returns a
 * clean clone (no behaviour change).
 */
export const buildHtmlRenderConfig = (
  config: Record<string, any>,
  schema: any,
  sectionId: number,
): Record<string, unknown> => {
  const translatable = getTranslatableFieldKeys(schema);
  const out: Record<string, unknown> = {};
  Object.keys(config || {}).forEach((k) => {
    if (k !== WIDGET_I18N_MAP_KEY) out[k] = config[k];
  });
  // Single-language shop → nothing to translate; ship the literal values as before.
  if (!isShopMultilingual()) return out;
  translatable.forEach((fieldKey) => {
    const raw = config[fieldKey];
    if (raw == null || String(raw) === '') return; // nothing to translate yet
    const key = ensureFieldTranslationKey(config, fieldKey, sectionId);
    out[fieldKey] = `{LANG_${key}}`;
  });
  return out;
};
